// The client of the account the site is showing (ADR-0062): a named group of connections and keys.
// `undefined` means "the server's default client": with one client nothing carries a `client` parameter,
// so a single-client account sees and sends exactly what it did before clients existed.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { getSiteClients, type SiteClient, type SiteClients } from "./api";
import { useHydrated } from "./hydrated";
import { CLIENTS_DEFAULT_MAX } from "./lib";

/** localStorage: the slug of the client chosen last. Read only after hydration (ADR-0050). */
export const CLIENT_STORAGE_KEY = "mk-client";

export const ClientSlugContext = createContext<string | undefined>(undefined);

/** The slug to send with key and connection calls; undefined when the account has one client. */
export const useClientSlug = (): string | undefined => useContext(ClientSlugContext);

/** The client the server treats as the default: `main`, else the oldest (the list is ordered by creation). */
export function defaultClient(clients: SiteClient[]): SiteClient | undefined {
  return clients.find((c) => c.slug === "main") ?? clients[0];
}

/** The client to show: the one named in the URL, else the remembered one, else the default. */
export function pickClient(clients: SiteClient[], fromUrl: string | null, remembered: string | null): SiteClient | undefined {
  return clients.find((c) => c.slug === fromUrl) ?? clients.find((c) => c.slug === remembered) ?? defaultClient(clients);
}

export function rememberedClient(): string | null {
  try {
    return window.localStorage.getItem(CLIENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function rememberClient(slug: string): void {
  try {
    window.localStorage.setItem(CLIENT_STORAGE_KEY, slug);
  } catch {
    /* private mode: the URL still carries the choice */
  }
}

/**
 * The account's clients and the one the page shows (ADR-0062). The choice lives in the URL (`?client=<slug>`) and in
 * localStorage `mk-client` (read only after hydration, ADR-0050). With one client nothing is chosen: `slug` stays
 * undefined and no call carries a `client`, so the account behaves as before clients existed.
 */
export function useActiveClient(enabled: boolean) {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const hydrated = useHydrated();
  const [list, setList] = useState<SiteClients | null>(null);

  const reload = useCallback(async (): Promise<SiteClients> => {
    let next: SiteClients = { clients: [], max: CLIENTS_DEFAULT_MAX };
    try {
      const r = await getSiteClients();
      if (r && Array.isArray(r.clients)) next = { clients: r.clients, max: typeof r.max === "number" ? r.max : CLIENTS_DEFAULT_MAX };
    } catch {
      /* the page works as for one client */
    }
    setList(next);
    return next;
  }, []);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  const clients = list?.clients ?? [];
  const urlClient = new URLSearchParams(search).get("client");
  const active = clients.length > 1 ? pickClient(clients, urlClient, hydrated ? rememberedClient() : null) : undefined;

  const go = useCallback(
    (slug: string, keepOther: boolean) => {
      const p = new URLSearchParams(keepOther ? search : "");
      p.set("client", slug);
      navigate({ pathname, search: `?${p.toString()}`, hash }, { replace: keepOther });
    },
    [navigate, pathname, search, hash],
  );

  // The URL and the remembered choice follow the client in use.
  useEffect(() => {
    if (!active) return;
    rememberClient(active.slug);
    if (urlClient !== active.slug) go(active.slug, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.slug, urlClient]);

  const select = useCallback(
    (slug: string) => {
      rememberClient(slug);
      go(slug, false);
    },
    [go],
  );

  return { ready: list !== null, clients, max: list?.max ?? CLIENTS_DEFAULT_MAX, active, slug: active?.slug, reload, select };
}
